import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  DataGrid,
  GridActionsCellItem,
  GridToolbarContainer,
  GridToolbarExport,
} from "@mui/x-data-grid";
import {
  Box,
  Paper,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  LinearProgress,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CardSearchInputsSessionFiltered from "./CardSearchInputsSessionFiltered";

export default function SessionManager() {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openView, setOpenView] = useState(false);
  const [openCreate, setOpenCreate] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [selectedSessionId, setSelectedSessionId] = useState(null);
  const [selectedSessionName, setSelectedSessionName] = useState("");
  const [sessionNameInput, setSessionNameInput] = useState("");
  const [activePollingSessionIds, setActivePollingSessionIds] = useState([]);

  const hasScrapingSession = useMemo(() => {
    return sessions.some((session) => {
      const status = String(session.status || "").toLowerCase();
      const total = Number(session.total_cards || 0);
      const scraped = Number(session.scraped_cards || 0);
      const failed = Number(session.failed_cards || 0);
      const scraping = Number(session.scraping_cards || 0);
      const processed = scraped + failed;
      return status === "scraping" || scraping > 0 || (total > 0 && processed < total && activePollingSessionIds.includes(session.id));
    });
  }, [sessions, activePollingSessionIds]);

  const fetchSessions = useCallback(async (showLoader = false) => {
    if (showLoader) {
      setLoading(true);
    }
    try {
      const res = await fetch("/api/search-sessions");
      const json = await res.json();
      const nextSessions = Array.isArray(json) ? json : [];
      setSessions(nextSessions);
      setActivePollingSessionIds((prev) => {
        if (!prev.length) return prev;
        const stillActive = prev.filter((sessionId) => {
          const session = nextSessions.find((item) => item.id === sessionId);
          if (!session) return false;
          const total = Number(session.total_cards || 0);
          const scraped = Number(session.scraped_cards || 0);
          const failed = Number(session.failed_cards || 0);
          const scraping = Number(session.scraping_cards || 0);
          const processed = scraped + failed;
          return scraping > 0 || (total > 0 && processed < total);
        });
        return stillActive.length === prev.length ? prev : stillActive;
      });
    } catch (err) {
      console.error("Failed to fetch sessions", err);
    } finally {
      if (showLoader) {
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    fetchSessions(true);
  }, [fetchSessions]);

  useEffect(() => {
    if (!hasScrapingSession) return undefined;
    const interval = setInterval(() => {
      fetchSessions(false);
    }, 2500);
    return () => clearInterval(interval);
  }, [hasScrapingSession]);


  const handleCreate = async () => {
    try {
      await fetch("/api/search-sessions", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_name: sessionNameInput }),
      });
      setOpenCreate(false);
      setSessionNameInput("");
      fetchSessions(true);
    } catch (err) {
      console.error("Failed to create session", err);
    }
  };

  const handleEdit = (session) => {
    setSelectedSessionId(session.id);
    setSelectedSessionName(session.session_name);
    setSessionNameInput(session.session_name);
    setOpenEdit(true);
  };

  const submitEdit = async () => {
    try {
      await fetch(`/api/search-sessions/${selectedSessionId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_name: sessionNameInput }),
      });
      setOpenEdit(false);
      setSessionNameInput("");
      fetchSessions(true);
    } catch (err) {
      console.error("Failed to update session", err);
    }
  };

  const handleView = (session) => {
    setSelectedSessionId(session.id);
    setSelectedSessionName(session.session_name);
    setOpenView(true);
  };

  const handleScrapeSession = async () => {
    try {
      const res = await fetch(`/api/run-scraper`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ search_session_id: selectedSessionId }),
      });
      const result = await res.json();
      if (res.ok) {
        setActivePollingSessionIds((prev) => (prev.includes(selectedSessionId) ? prev : [...prev, selectedSessionId]));
        alert(`Session scrape started: ${result.message}`);
        fetchSessions(false);
      } else {
        alert(`Scrape failed: ${result.error}`);
      }
    } catch (err) {
      console.error("Scrape session failed", err);
      alert("Scrape session error");
    }
  };

  const renderProgressCell = (params) => {
    const total = Number(params.row.total_cards || 0);
    const scraped = Number(params.row.scraped_cards || 0);
    const failed = Number(params.row.failed_cards || 0);
    const processed = scraped + failed;
    const percent = total > 0 ? Math.min(100, Math.round((processed / total) * 100)) : 0;
    const label = total > 0 ? `${scraped}/${total} scraped` : "0/0 scraped";

    return (
      <Box sx={{ width: "100%", py: 0.5 }}>
        <Typography variant="caption" sx={{ display: "block", fontWeight: 600 }}>
          {label}
        </Typography>
        <LinearProgress variant="determinate" value={percent} />
      </Box>
    );
  };

  const columns = [
    { field: "id", headerName: "ID", width: 90 },
    { field: "session_name", headerName: "Session Name", width: 250, headerClassName: "green-header" },
    {
      field: "status",
      headerName: "Status",
      width: 130,
      renderCell: (params) => {
        const colorMap = {
          completed: "green",
          failed: "red",
          pending: "orange",
          scraping: "#1976d2",
        };
        return (
          <span style={{ color: colorMap[params.value] || "inherit" }}>
            {params.value}
          </span>
        );
      },
    },
    {
      field: "session_progress",
      headerName: "Progress",
      width: 220,
      sortable: false,
      renderCell: renderProgressCell,
    },
    { field: "created_at", headerName: "Created At", width: 180 },
    {
      field: "actions",
      type: "actions",
      width: 100,
      getActions: (params) => [
        <GridActionsCellItem
          icon={<VisibilityIcon />}
          label="View"
          onClick={() => handleView(params.row)}
        />,
        <GridActionsCellItem
          icon={<EditIcon />}
          label="Edit"
          onClick={() => handleEdit(params.row)}
        />,
      ],
    },
  ];

  return (
    <Paper sx={{ p: 2 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
        <Typography variant="h5">Search Sessions</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setOpenCreate(true)}
        >
          New Session
        </Button>
      </Box>

      <DataGrid
        rows={sessions}
        columns={columns}
        loading={loading}
        pageSize={10}
        rowsPerPageOptions={[10, 25]}
        getRowId={(row) => row.id}
        autoHeight
        components={{ Toolbar: GridToolbarContainer }}
        componentsProps={{
          toolbar: {
            children: <GridToolbarExport csvOptions={{ fileName: "sessions" }} />,
          },
        }}
      />

      <Dialog open={openView} onClose={() => setOpenView(false)} maxWidth="lg" fullWidth>
        <DialogTitle>Cards in Session {selectedSessionId}</DialogTitle>
        <DialogContent>
          <CardSearchInputsSessionFiltered
            search_session_id={selectedSessionId}
            session_name={selectedSessionName}
            onSessionProgressChange={(sessionProgress) => {
              if (sessionProgress?.id) {
                const total = Number(sessionProgress.total_cards || 0);
                const scraped = Number(sessionProgress.scraped_cards || 0);
                const failed = Number(sessionProgress.failed_cards || 0);
                const scraping = Number(sessionProgress.scraping_cards || 0);
                const processed = scraped + failed;
                setActivePollingSessionIds((prev) => {
                  const shouldTrack = scraping > 0 || (total > 0 && processed < total);
                  if (shouldTrack) {
                    return prev.includes(sessionProgress.id) ? prev : [...prev, sessionProgress.id];
                  }
                  return prev.filter((id) => id !== sessionProgress.id);
                });
              }
              fetchSessions(false);
            }}
          />
          <Box sx={{ mt: 2 }}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleScrapeSession}
              disabled={!selectedSessionId}
            >
              Scrape Session
            </Button>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenView(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openCreate} onClose={() => setOpenCreate(false)}>
        <DialogTitle>Create New Session</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <TextField
              fullWidth
              label="Session Name"
              value={sessionNameInput}
              onChange={(e) => setSessionNameInput(e.target.value)}
            />
            <Button
              sx={{ mt: 2 }}
              variant="contained"
              onClick={handleCreate}
              disabled={!sessionNameInput.trim()}
            >
              Save
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      <Dialog open={openEdit} onClose={() => setOpenEdit(false)}>
        <DialogTitle>Edit Session</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <TextField
              fullWidth
              label="Session Name"
              value={sessionNameInput}
              onChange={(e) => setSessionNameInput(e.target.value)}
            />
            <Button
              sx={{ mt: 2 }}
              variant="contained"
              onClick={submitEdit}
              disabled={!sessionNameInput.trim()}
            >
              Update
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    </Paper>
  );
}
